import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import {
  getTournamentBracket,
  updateTournamentMatchParticipants,
  updateTournamentMatchScore,
  type AdminTournamentBracket,
  type AdminTournamentBracketMatch,
  type AdminTournamentCategoryMatchSettings,
} from "../services/eventRegistrationAdminService";
import type { AdminTournamentManualPair } from "./useAdminTournamentResources";
import {
  findMatchInBracket,
  isFinalRoundCode,
  scoreDraftForMatch,
} from "../utils/tournamentOperations";
import type { AdminTournamentMatchupModalState } from "../components/AdminTournamentMatchupModal";
import type { AdminTournamentScoreModalState } from "../components/AdminTournamentScoreModal";

type UseAdminTournamentMatchModalsInput = {
  bracket: AdminTournamentBracket | null;
  categoryKey: string;
  firstKnockoutRoundNumber: number;
  loadSchedule: () => Promise<void>;
  manualPairs: AdminTournamentManualPair[];
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  setBracket: Dispatch<SetStateAction<AdminTournamentBracket | null>>;
  setSaving: Dispatch<SetStateAction<boolean>>;
  settings: AdminTournamentCategoryMatchSettings | null;
  token: string;
  tournamentId: number;
};

export function useAdminTournamentMatchModals({
  bracket,
  categoryKey,
  firstKnockoutRoundNumber,
  loadSchedule,
  manualPairs,
  onError,
  onSuccess,
  setBracket,
  setSaving,
  settings,
  token,
  tournamentId,
}: UseAdminTournamentMatchModalsInput) {
  const [scoreModal, setScoreModal] =
    useState<AdminTournamentScoreModalState | null>(null);
  const [matchupModal, setMatchupModal] =
    useState<AdminTournamentMatchupModalState | null>(null);

  const openScoreModal = useCallback(
    async (
      match: AdminTournamentBracketMatch,
      overrideCategoryKey = categoryKey,
    ) => {
      if (!Number(match.id || 0)) {
        onError?.("Partida inválida para informar resultado.");
        return;
      }
      const modalCategoryKey = String(overrideCategoryKey || categoryKey || "");
      let matchForScore = match;
      if (modalCategoryKey) {
        const loadedMatch =
          modalCategoryKey === categoryKey
            ? findMatchInBracket(bracket, Number(match.id))
            : null;
        if (loadedMatch) {
          matchForScore = loadedMatch;
        } else {
          try {
            const loadedBracket = await getTournamentBracket(
              token,
              tournamentId,
              modalCategoryKey,
            );
            const bracketMatch = findMatchInBracket(
              loadedBracket,
              Number(match.id),
            );
            if (bracketMatch) matchForScore = bracketMatch;
            if (modalCategoryKey === categoryKey) setBracket(loadedBracket);
          } catch (_error) {
            matchForScore = match;
          }
        }
      }
      const isFinal = isFinalRoundCode(matchForScore.roundCode);
      const bestOfSets = isFinal
        ? Number(settings?.finalBestOfSets || settings?.bestOfSets || 3)
        : Number(settings?.bestOfSets || 3);
      setScoreModal({
        match: matchForScore,
        categoryKey: modalCategoryKey,
        title: `${matchForScore.sideALabel || "A definir"} x ${
          matchForScore.sideBLabel || "A definir"
        }`,
        sets: scoreDraftForMatch(matchForScore, bestOfSets),
      });
    },
    [bracket, categoryKey, onError, setBracket, settings, token, tournamentId],
  );

  const canEditMatchup = useCallback(
    (match: AdminTournamentBracketMatch) =>
      String(match.stage || "").trim().toLowerCase() === "knockout" &&
      Number(match.roundNumber || 0) === firstKnockoutRoundNumber &&
      ["pending", "scheduled"].includes(
        String(match.status || "").trim().toLowerCase(),
      ),
    [firstKnockoutRoundNumber],
  );

  const openMatchupModal = useCallback(
    (match: AdminTournamentBracketMatch) => {
      if (!canEditMatchup(match)) {
        onError?.(
          "Só é possível editar confrontos do primeiro mata-mata antes do jogo começar.",
        );
        return;
      }
      const sideARegistrationId =
        Number(match.sideARegistrationId || 0) > 0
          ? String(match.sideARegistrationId)
          : String(manualPairs[0]?.registrationId || "");
      const sideBRegistrationId =
        Number(match.sideBRegistrationId || 0) > 0
          ? String(match.sideBRegistrationId)
          : String(
              manualPairs.find(
                (pair) => String(pair.registrationId) !== sideARegistrationId,
              )?.registrationId || "",
            );
      setMatchupModal({ match, sideARegistrationId, sideBRegistrationId });
    },
    [canEditMatchup, manualPairs, onError],
  );

  const saveMatchupModal = useCallback(async () => {
    if (!matchupModal) return;
    const sideARegistrationId = Number(matchupModal.sideARegistrationId || 0);
    const sideBRegistrationId = Number(matchupModal.sideBRegistrationId || 0);
    if (!sideARegistrationId || !sideBRegistrationId) {
      onError?.("Selecione as duas duplas do confronto.");
      return;
    }
    if (sideARegistrationId === sideBRegistrationId) {
      onError?.("A mesma dupla não pode ocupar os dois lados.");
      return;
    }
    setSaving(true);
    try {
      const next = await updateTournamentMatchParticipants(
        token,
        tournamentId,
        categoryKey,
        matchupModal.match.id,
        { sideARegistrationId, sideBRegistrationId },
      );
      setBracket(next);
      setMatchupModal(null);
      await loadSchedule();
      onSuccess?.("Confronto atualizado.");
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível atualizar o confronto.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    categoryKey,
    loadSchedule,
    matchupModal,
    onError,
    onSuccess,
    setBracket,
    setSaving,
    token,
    tournamentId,
  ]);

  const saveScoreModal = useCallback(async () => {
    if (!scoreModal) return;
    const parsedSets = scoreModal.sets
      .map((set, index) => {
        const rawGamesA = set.gamesA === "" ? null : Number(set.gamesA);
        const rawGamesB = set.gamesB === "" ? null : Number(set.gamesB);
        const rawTieBreakA =
          set.tieBreakA === "" ? null : Number(set.tieBreakA);
        const rawTieBreakB =
          set.tieBreakB === "" ? null : Number(set.tieBreakB);

        if (set.isMatchTieBreak) {
          const tieBreakA = Number.isFinite(rawTieBreakA)
            ? rawTieBreakA
            : rawGamesA;
          const tieBreakB = Number.isFinite(rawTieBreakB)
            ? rawTieBreakB
            : rawGamesB;
          if (
            !Number.isFinite(tieBreakA) ||
            !Number.isFinite(tieBreakB) ||
            tieBreakA === tieBreakB
          ) {
            return null;
          }
          const hasVisibleGames =
            Number.isFinite(rawGamesA) &&
            Number.isFinite(rawGamesB) &&
            rawGamesA !== rawGamesB;
          const gamesA = hasVisibleGames
            ? rawGamesA
            : tieBreakA > tieBreakB
              ? 1
              : 0;
          const gamesB = hasVisibleGames
            ? rawGamesB
            : tieBreakB > tieBreakA
              ? 1
              : 0;
          return {
            setNumber: index + 1,
            gamesA,
            gamesB,
            tieBreakA,
            tieBreakB,
            isMatchTieBreak: true,
            winnerSide: tieBreakA > tieBreakB ? ("A" as const) : ("B" as const),
          };
        }

        const gamesA = rawGamesA;
        const gamesB = rawGamesB;
        if (
          !Number.isFinite(gamesA) ||
          !Number.isFinite(gamesB) ||
          gamesA === gamesB
        ) {
          return null;
        }
        const tieBreakA = rawTieBreakA;
        const tieBreakB = rawTieBreakB;
        return {
          setNumber: index + 1,
          gamesA,
          gamesB,
          tieBreakA: Number.isFinite(tieBreakA) ? tieBreakA : null,
          tieBreakB: Number.isFinite(tieBreakB) ? tieBreakB : null,
          isMatchTieBreak: set.isMatchTieBreak,
          winnerSide: gamesA > gamesB ? ("A" as const) : ("B" as const),
        };
      })
      .filter(Boolean);
    if (!parsedSets.length) {
      onError?.("Informe ao menos um set válido, sem empate.");
      return;
    }
    setSaving(true);
    try {
      const next = await updateTournamentMatchScore(
        token,
        tournamentId,
        scoreModal.categoryKey,
        scoreModal.match.id,
        { sets: parsedSets },
      );
      if (scoreModal.categoryKey === categoryKey) setBracket(next);
      setScoreModal(null);
      await loadSchedule();
      onSuccess?.("Resultado salvo.");
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível salvar o resultado.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    categoryKey,
    loadSchedule,
    onError,
    onSuccess,
    scoreModal,
    setBracket,
    setSaving,
    token,
    tournamentId,
  ]);

  return {
    canEditMatchup,
    matchupModal,
    openMatchupModal,
    openScoreModal,
    saveMatchupModal,
    saveScoreModal,
    scoreModal,
    setMatchupModal,
    setScoreModal,
  };
}
